import { useAuth } from "@/hooks/useAuth";
import { trackReportVote } from "@/lib/analytics";
import { castVote, getUserVote } from "@/lib/votes";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import { Box, IconButton, Typography } from "@mui/material";
import { useEffect, useState } from "react";

interface VoteButtonsProps {
  reportId: string;
  confirmCount: number;
  denyCount: number;
  onAuthRequired: () => void;
}

export default function VoteButtons({
  reportId,
  confirmCount,
  denyCount,
  onAuthRequired,
}: VoteButtonsProps) {
  const { user } = useAuth();
  const [userVote, setUserVote] = useState<"confirm" | "deny" | null>(null);
  const [voting, setVoting] = useState(false);
  const [localConfirm, setLocalConfirm] = useState(confirmCount);
  const [localDeny, setLocalDeny] = useState(denyCount);

  // Sync from props when server data updates
  useEffect(() => {
    setLocalConfirm(confirmCount);
  }, [confirmCount]);
  useEffect(() => {
    setLocalDeny(denyCount);
  }, [denyCount]);

  useEffect(() => {
    if (!user) return;
    getUserVote(reportId, user.uid)
      .then((v) => setUserVote(v?.voteType ?? null))
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("Failed to fetch user vote:", err);
        setUserVote(null);
      });
  }, [reportId, user]);

  const handleVote = async (type: "confirm" | "deny") => {
    if (!user) {
      onAuthRequired();
      return;
    }
    if (voting) return;
    setVoting(true);

    const prevVote = userVote;
    const isToggleOff = prevVote === type;
    const newVote = isToggleOff ? null : type;

    // Optimistic update
    setUserVote(newVote);
    if (type === "confirm") {
      setLocalConfirm((c) => c + (isToggleOff ? -1 : 1));
      if (prevVote === "deny") setLocalDeny((c) => c - 1);
    } else {
      setLocalDeny((c) => c + (isToggleOff ? -1 : 1));
      if (prevVote === "confirm") setLocalConfirm((c) => c - 1);
    }

    try {
      await castVote(reportId, user.uid, type);
      trackReportVote(type);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to cast vote:", err);
      setUserVote(prevVote);
      setLocalConfirm(confirmCount);
      setLocalDeny(denyCount);
    } finally {
      setVoting(false);
    }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
        <IconButton
          size="small"
          onClick={() => handleVote("confirm")}
          disabled={voting}
          sx={{
            color: userVote === "confirm" ? "kelp.main" : "text.secondary",
            p: 0.5,
            "&:hover": { color: "kelp.main" },
          }}
        >
          <ThumbUpIcon sx={{ fontSize: 14 }} />
        </IconButton>
        <Typography sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
          {localConfirm}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
        <IconButton
          size="small"
          onClick={() => handleVote("deny")}
          disabled={voting}
          sx={{
            color: userVote === "deny" ? "buoy.main" : "text.secondary",
            p: 0.5,
            "&:hover": { color: "buoy.main" },
          }}
        >
          <ThumbDownIcon sx={{ fontSize: 14 }} />
        </IconButton>
        <Typography sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
          {localDeny}
        </Typography>
      </Box>
    </Box>
  );
}
